import { Injectable, Injector, OnDestroy } from '@angular/core';
import {
  Observable,
  BehaviorSubject,
  of,
  Subscription,
  forkJoin,
  throwError,
} from 'rxjs';
import {
  map,
  catchError,
  switchMap,
  finalize,
  shareReplay,
} from 'rxjs/operators';
import { UserModel } from '../models/user.model';
import { AuthModel } from '../models/auth.model';
import { AuthHTTPService } from './auth-http';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { AuthenticationModel } from '../models/authentication.model';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import Swal from 'sweetalert2';
import { SetPasswordModel } from '../models/setPassword.model';
import { ResetPasswordModel } from '../models/resetPassword.model';
import { Role } from '../models/role';
import { ConfirmUserModel } from '../models/confirmUser.model';
import { AuthenticationConfirmEmailModel } from '../models/authenticationConfirmEmail.model';
import { RegisterRevealModel } from '../models/RegisterRevealModel';
import { JtrServicesModel } from '../models/jtrServicesModel';
import { ServiceTypesModel } from '../models/serviceTypesModel';
import { RegisterRevealFormSaveModel } from '../models/RegisterRevealFormSaveModel';
import { SsoUserInfo } from '../models/sso-user.model';
import { FileAttachmentService } from './file-attachment.service';

const isDevMode = !environment.production;
import { ActiveProjectWorkflowService } from 'src/app/pages/project-management/services/active-project-workflow.service';

export type UserType = UserModel | undefined;

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  // private fields
  private unsubscribe: Subscription[] = []; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/
  private authLocalStorageToken: string;

  /** Aynı anda birden fazla refreshUserData çağrısını tek HTTP isteğinde birleştirir */
  private userRefreshInFlight$: Observable<any> | null = null;

  /** Aynı anda birden fazla 401'in (veya proaktif zamanlayıcının) paralel RefreshToken
   *  çağrısı yapmasını önler — hepsi aynı isteğin sonucunu paylaşır. */
  private refreshTokenInProgress$: Observable<UserType> | null = null;
  private proactiveRefreshTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly currentUserStorageKey = 'currentUser';
  private readonly refreshTokenEndpoint = `${environment.apiUrl}/api/identity/RefreshToken`;
  private readonly logoutEndpoint = `${environment.apiUrl}/api/identity/Logout`;
  /** CG-WEB-004: ForgotPassword hatalarında interceptor'ın ham hata toast'ı e-posta
   *  varlığını sızdırmasın diye genel hata toast'ını bastırır (bkz. ErrorInterceptor). */
  private readonly skipErrorToastHeader = 'x-cg-skip-error-toast';
  /** Access token süresi dolmadan bu kadar önce proaktif yenileme tetiklenir. */
  private readonly proactiveRefreshSkewMs = 2 * 60 * 1000;
  private readonly minProactiveRefreshDelayMs = 5000;
  /** Birden fazla tab aynı rotating refresh token'ı aynı anda kullanmasın diye basit kilit. */
  private readonly refreshLockKey = 'refreshTokenLock';
  private readonly refreshLockTtlMs = 10000;

  /** CG-WEB-006: MFA ara aşama (şifre doğrulandı, OTP bekleniyor) challenge token'ı.
   *  currentUserSubject'e YAZILMAZ — login ekranındaki "zaten girişliyim" yönlendirmesi
   *  yarım oturumla tetiklenmesin. sessionStorage: sekmeye özel, F5'te yaşar. */
  private readonly mfaPendingTokenKey = 'mfaPendingToken';

  /** Cross-browser: sekme tekrar görünür olunca sunucudaki efektif şirketi doğrula. */
  private readonly companySelectRoute = '/applicationadmin/company-select';
  private readonly applicationSelectRoute =
    '/applicationadmin/application-select';
  private readonly applicationAdminSelectionFlag =
    'applicationAdminCompanySelected';
  private readonly selectedCompanyKey = 'applicationAdminSelectedCompanyId';
  private readonly superAdminSelectionFlag = 'superAdminApplicationSelected';
  private readonly selectedApplicationKey = 'superAdminSelectedApplicationId';
  private readonly emptyGuid = '00000000-0000-0000-0000-000000000000';
  private readonly userRefreshDataEndpoint = `${environment.apiUrl}/api/identity/UserRefreshData`;
  private readonly currentCompanyIdEndpoint = `${environment.apiUrl}/api/profile/GetCurrentCompanyId`;
  private readonly companyContextCheckMinIntervalMs = 15_000;
  private readonly companyContextCheckDebounceMs = 400;
  private companyContextCheckTimer: ReturnType<typeof setTimeout> | null = null;
  private companyContextCheckInFlight = false;
  private lastCompanyContextCheckAt = 0;
  private readonly onDocumentVisibilityForCompanyContext = () => {
    if (document.visibilityState === 'visible') {
      this.scheduleServerCompanyContextCheck();
    }
  };
  private readonly onWindowFocusForCompanyContext = () => {
    this.scheduleServerCompanyContextCheck();
  };

  // public fields
  currentUser$: Observable<UserType>;
  isLoading$: Observable<boolean>;
  currentUserSubject: BehaviorSubject<UserType>;
  isLoadingSubject: BehaviorSubject<boolean>;

  get currentUserValue(): UserType {
    return this.currentUserSubject.value;
  }

  set currentUserValue(user: UserType) {
    this.currentUserSubject.next(user);
  }

  /**
   * Logout geçişinde true: sidebar/permission UI, grants temizlenirken
   * menüyü yeniden çizmesin (flash). Token/storage yine hemen silinir.
   */
  private logoutInProgress = false;

  get isLogoutInProgress(): boolean {
    return this.logoutInProgress;
  }

  /**
   * Logout / session expire sırasında açık kalan NgbModal + SweetAlert
   * overlay'lerini kapatır; login ekranı modalın altında kalmasın.
   */
  private closeOpenOverlays(): void {
    try {
      const modalService = this.injector.get(NgbModal);
      if (modalService.hasOpenModals()) {
        modalService.dismissAll('logout');
      }
    } catch {
      /* NgbModal henüz hazır değilse yoksay */
    }

    try {
      if (Swal.isVisible()) {
        Swal.close();
      }
    } catch {
      /* ignore */
    }

    // Bazı edge case'lerde backdrop / body class kalabiliyor
    try {
      document.body.classList.remove('modal-open');
      document.querySelectorAll('ngb-modal-window, ngb-modal-backdrop').forEach(
        (el) => el.remove(),
      );
      document.querySelectorAll('.modal-backdrop').forEach((el) => el.remove());
    } catch {
      /* ignore */
    }
  }

  private readonly emptyAttachmentGuid = '00000000-0000-0000-0000-000000000000';

  constructor(
    private authHttpService: AuthHTTPService,
    private router: Router,
    private http: HttpClient,
    private toastrService: ToastrService,
    private fileAttachmentService: FileAttachmentService,
    /** Lazy workflow temizliği için — doğrudan DI döngüsünü önler (DataService → AuthService). */
    private injector: Injector,
  ) {
    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    const initialUser = this.loadStoredUserOrForceLegacyLogout();
    this.currentUserSubject = new BehaviorSubject<UserType>(initialUser);
    this.currentUser$ = this.currentUserSubject.asObservable();
    this.isLoading$ = this.isLoadingSubject.asObservable();

    this.setupMultiTabSync();
    this.setupCrossBrowserCompanyContextGuard();
    if (initialUser) {
      this.scheduleProactiveRefresh(initialUser);
    }

    //const subscr = this.getUserByToken().subscribe();
    //this.unsubscribe.push(subscr);
  }

  /**
   * Refresh-token mekanizması eklenmeden önce oluşturulmuş oturumlar (currentUser'da
   * token var ama refreshToken yok) artık geçersiz sayılır: kullanıcı yeniden login
   * olmalı ki rotating refresh token akışına dahil olsun.
   */
  private loadStoredUserOrForceLegacyLogout(): UserType {
    let raw: string | null = null;
    try {
      raw = localStorage.getItem(this.currentUserStorageKey);
    } catch {
      return undefined;
    }

    if (!raw) {
      return undefined;
    }

    let user: any;
    try {
      user = JSON.parse(raw);
    } catch {
      return undefined;
    }

    if (user?.token && !user?.refreshToken) {
      try {
        localStorage.removeItem(this.currentUserStorageKey);
      } catch {
        /* ignore */
      }
      return undefined;
    }

    return user;
  }

  /**
   * Bir tab'da login/refresh/logout olduğunda diğer açık tab'ların da haberdar
   * olması için localStorage 'storage' event'i dinlenir (localStorage seçildiği
   * için bu event tab'lar arası otomatik gelir).
   */
  private setupMultiTabSync(): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key !== this.currentUserStorageKey) {
        return;
      }

      if (!event.newValue) {
        this.logoutInProgress = true;
        this.closeOpenOverlays();
        this.clearProactiveRefreshTimer();
        this.refreshTokenInProgress$ = null;
        this.currentUserSubject.next(undefined);
        if (!this.router.url.startsWith('/auth')) {
          this.router.navigate(['/auth/loginjtr']).then(
            () => {
              this.logoutInProgress = false;
            },
            () => {
              this.logoutInProgress = false;
            },
          );
        } else {
          this.logoutInProgress = false;
        }
        return;
      }

      try {
        const updatedUser = JSON.parse(event.newValue);
        this.currentUserSubject.next(updatedUser);
        this.scheduleProactiveRefresh(updatedUser);
      } catch {
        /* diğer tab'dan gelen bozuk payload'u yoksay */
      }
    });
  }

  /**
   * Farklı tarayıcı / cihaz: localStorage paylaşılmadığı için storage event gelmez.
   * Sekme tekrar görünür/odaklandığında UserRefreshData ile sunucudaki efektif
   * şirketi okuyup bu sekmenin session seçimiyle karşılaştırır; uyuşmazsa
   * company-select'e atar (cross-tab JwtInterceptor ile aynı UX).
   *
   * Backend (UserRefreshData / login): ApplicationAdmin için efektif şirket
   * `companyId` + `previewCompanyId` (+ `companyName`) olarak döner. Preview
   * yoksa null — şirket seçimi yeniden zorunlu.
   */
  private setupCrossBrowserCompanyContextGuard(): void {
    if (typeof document === 'undefined' || typeof window === 'undefined') {
      return;
    }

    document.addEventListener(
      'visibilitychange',
      this.onDocumentVisibilityForCompanyContext,
    );
    window.addEventListener('focus', this.onWindowFocusForCompanyContext);
  }

  private scheduleServerCompanyContextCheck(): void {
    if (this.companyContextCheckTimer) {
      clearTimeout(this.companyContextCheckTimer);
    }
    this.companyContextCheckTimer = setTimeout(() => {
      this.companyContextCheckTimer = null;
      this.runServerCompanyContextCheck();
    }, this.companyContextCheckDebounceMs);
  }

  private runServerCompanyContextCheck(): void {
    if (this.logoutInProgress || this.companyContextCheckInFlight) {
      return;
    }

    const user = this.currentUserValue;
    const isSuperAdmin = user?.roles?.includes('SuperAdmin');
    const isApplicationAdmin = user?.roles?.includes('ApplicationAdmin');
    const isSustainabilityTeam = user?.roles?.includes('SustainabilityTeam');
    if (
      !user?.token ||
      (!isApplicationAdmin && !isSuperAdmin && !isSustainabilityTeam)
    ) {
      return;
    }

    const url = this.router.url || '';
    if (
      url.startsWith('/auth') ||
      url.startsWith(this.companySelectRoute) ||
      url.startsWith(this.applicationSelectRoute)
    ) {
      return;
    }

    if (sessionStorage.getItem(this.applicationAdminSelectionFlag) !== 'true') {
      return;
    }

    const tabCompanyId = String(
      sessionStorage.getItem(this.selectedCompanyKey) || '',
    ).trim();
    if (!tabCompanyId) {
      return;
    }

    const now = Date.now();
    if (now - this.lastCompanyContextCheckAt < this.companyContextCheckMinIntervalMs) {
      return;
    }

    this.companyContextCheckInFlight = true;
    this.lastCompanyContextCheckAt = now;

    this.http
      .get<{ companyId?: string; CompanyId?: string }>(
        this.currentCompanyIdEndpoint,
      )
      .pipe(
        catchError(() => of(null)),
        finalize(() => {
          this.companyContextCheckInFlight = false;
        }),
      )
      .subscribe((data) => {
        if (!data || this.logoutInProgress) {
          return;
        }

        // Hâlâ aynı korumalı ekranda mıyiz — async cevap gecikmesinde yarış olmasın.
        const currentUrl = this.router.url || '';
        if (
          currentUrl.startsWith('/auth') ||
          currentUrl.startsWith(this.companySelectRoute) ||
          currentUrl.startsWith(this.applicationSelectRoute)
        ) {
          return;
        }

        const serverCompanyId = String(
          data.companyId ?? data.CompanyId ?? '',
        ).trim();
        const latestTabCompanyId = String(
          sessionStorage.getItem(this.selectedCompanyKey) || '',
        ).trim();

        // Preview sunucuda yoksa (null) ama bu sekme seçimi tamam sanıyorsa
        // yeniden seçim zorunlu — yanlış pozitif değil, bilinçli backend sözleşmesi.
        if (!serverCompanyId || !latestTabCompanyId || serverCompanyId !== latestTabCompanyId) {
          sessionStorage.removeItem(this.applicationAdminSelectionFlag);
          this.closeOpenOverlays();
          // Hafif endpoint yalnızca değişimi saptar. Company-select eski
          // local kullanıcı bağlamını göstermesin diye, yönlendirmeden önce
          // UserRefreshData ile efektif şirket/profil bilgisini yenile.
          this.refreshUserData()
            .pipe(catchError(() => of(null)))
            .subscribe(() => {
              this.router.navigate([this.companySelectRoute], {
                queryParams: { reason: 'company-context-changed' },
              });
            });
        }
      });
  }

  /**
   * UserRefreshData / login: ApplicationAdmin efektif şirket.
   * Öncelik previewCompanyId, sonra companyId (backend her ikisini de doldurur).
   */
  private extractEffectiveCompanyId(data: any): string {
    if (!data || typeof data !== 'object') {
      return '';
    }
    const raw =
      data.previewCompanyId ||
      data.companyId ||
      data.previewCustomerId ||
      data.branchId ||
      data.user?.previewCompanyId ||
      data.user?.companyId ||
      data.user?.branchId ||
      '';
    return String(raw || '').trim();
  }

  /**
   * UserRefreshData / login: SuperAdmin efektif uygulama.
   * Öncelik previewApplicationId; yedek olarak (boş guid değilse) applicationId.
   */
  private extractEffectiveApplicationId(data: any): string {
    if (!data || typeof data !== 'object') {
      return '';
    }
    const raw =
      data.previewApplicationId || data.user?.previewApplicationId || '';
    if (raw) {
      return String(raw).trim();
    }
    const fallback = String(
      data.applicationId || data.user?.applicationId || '',
    ).trim();
    return fallback && fallback !== this.emptyGuid ? fallback : '';
  }

  /**
   *  Confirms if user is admin
   */
  get isAdmin() {
    // @ts-ignore
    if (this.currentUserSubject?.value.roles === undefined) {
      return false;
    }

    return (
      this.currentUser$ &&
      this.currentUserSubject.value.roles.includes(Role.Admin)
    );
  }

  /**
   *  Confirms if user is client
   */
  get isClient() {
    // @ts-ignore
    if (this.currentUserSubject?.value.roles === undefined) {
      return false;
    }

    return !(
      this.currentUser$ &&
      this.currentUserSubject.value.roles.includes(Role.Admin)
    );
  }

  // public methods
  login(email: string, password: string): Observable<UserType> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.login(email, password).pipe(
      map((auth: AuthModel) => {
        const result = this.setAuthFromLocalStorage(auth);
        return result;
      }),
      switchMap(() => this.getUserByToken()),
      catchError((err) => {
        console.error('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false)),
    );
  }

  loginreal(
    email: string,
    password: string,
    language: 'tr' | 'en' = 'tr',
    tenantKey?: string,
  ) {
    let loginData = new AuthenticationModel(email, password, language);
    this.isLoadingSubject.next(true);
    // Backend tenant'i YALNIZCA ?tenant= query string'inden (yoksa subdomain'den)
    // okur (bkz. TenantMiddleware.cs + IdentityController.cs:119 — Tenant alani
    // [JsonIgnore], body/header'dan asla atanmaz). Secili tenant bu yuzden URL'e
    // query param olarak eklenir; secim yoksa hic eklenmez (diger environment'larda
    // davranis degismez).
    const url = tenantKey
      ? `${environment.apiUrl}/api/identity/Login?tenant=${encodeURIComponent(tenantKey)}`
      : `${environment.apiUrl}/api/identity/Login`;
    return this.http
      .post<any>(url, loginData)
      .pipe(
        map((user) => {
          // login successful if there's a jwt token in the response.
          // CG-WEB-006: MFA aktifken backend token YERİNE
          // { requiresTwoFactor: true, mfaToken } döner — oturum persist edilmez,
          // component yanıtı görüp /auth/verify-otp'a yönlendirir.
          if (user && user.token) {
            this.applyAuthenticatedSession(user);
          }
          return user;
        }),
        finalize(() => this.isLoadingSubject.next(false)),
      );
  }

  /** CG-WEB-006: MFA ikinci adım — challenge token + e-postadaki kod ile asıl JWT alınır. */
  verifyOtp(code: string, language: 'tr' | 'en' = 'tr') {
    this.isLoadingSubject.next(true);
    return this.http
      .post<any>(`${environment.apiUrl}/api/identity/VerifyOtp`, {
        mfaToken: this.getMfaPendingToken(),
        code,
        language,
      })
      .pipe(
        map((user) => {
          if (user && user.token) {
            this.clearMfaPendingToken();
            this.applyAuthenticatedSession(user);
          }
          return user;
        }),
        finalize(() => this.isLoadingSubject.next(false)),
      );
  }

  /** CG-WEB-006: geçerli challenge token ile yeni OTP e-postası ister. */
  resendOtp(language: 'tr' | 'en' = 'tr') {
    return this.http.post<any>(`${environment.apiUrl}/api/identity/ResendOtp`, {
      mfaToken: this.getMfaPendingToken(),
      language,
    });
  }

  setMfaPendingToken(token: string): void {
    sessionStorage.setItem(this.mfaPendingTokenKey, token);
  }

  getMfaPendingToken(): string | null {
    return sessionStorage.getItem(this.mfaPendingTokenKey);
  }

  clearMfaPendingToken(): void {
    sessionStorage.removeItem(this.mfaPendingTokenKey);
  }

  /**
   * Başarılı kimlik doğrulamanın ortak oturum kurulumu (loginreal MFA'sız yol + verifyOtp):
   * storage persist + subject + proaktif refresh zamanlayıcısı + karşılama toast'ı.
   */
  private applyAuthenticatedSession(user: any): void {
    // store user details and jwt token in local storage to keep user logged in between page refreshes
    this.persistCurrentUserSafely(user);
    this.currentUserSubject.next(user);
    this.scheduleProactiveRefresh(user);

    // Display welcome toast!
    setTimeout(() => {
      this.toastrService.success(
        'You have successfully logged in ' +
          ' Carbon Gate. Now you can start to explore. Enjoy! 🎉',
        '👋 Welcome, ' + user.name + '!',
        { closeButton: true },
      );
    }, 2000);
  }

  /**
   * Login sonrası rol bazlı yönlendirme (LoginjtrComponent'ten taşındı; verify-otp da kullanır).
   * returnUrl verilirse rol bakılmaksızın oraya gidilir (ör. change-email → /profile/edit).
   */
  redirectAfterLogin(returnUrl?: string): void {
    const currentUser =
      this.currentUserValue ||
      JSON.parse(<string>localStorage.getItem('currentUser'));
    const isApplicationAdmin = currentUser?.roles?.includes('ApplicationAdmin');
    const isSuperAdmin = currentUser?.roles?.includes('SuperAdmin');

    sessionStorage.removeItem(this.applicationAdminSelectionFlag);
    sessionStorage.removeItem(this.selectedCompanyKey);
    sessionStorage.removeItem(this.superAdminSelectionFlag);
    sessionStorage.removeItem(this.selectedApplicationKey);
    sessionStorage.removeItem('authSessionUserIdentity');
    localStorage.removeItem('applicationAdminActiveCompanyId');
    localStorage.removeItem('superAdminActiveApplicationId');

    if (returnUrl) {
      this.router.navigateByUrl(returnUrl);
      return;
    }

    if (isSuperAdmin) {
      this.router.navigate(['/applicationadmin/application-select']);
    } else if (isApplicationAdmin) {
      this.router.navigate(['/applicationadmin/company-select']);
    } else {
      this.router.navigate(['/home']);
    }
  }

  /**
   * Login ekranı, bu kurulum/tenant için LDAP login'in açık olup olmadığını
   * backend'den öğrenir (GET /api/identity/LoginOptions, anonim). LDAP açıkken
   * login formu kullanıcı adı (uid) girişine de izin verir. tenantKey verilirse
   * (tenant dropdown'u olan kurulumlarda) loginreal() ile aynı ?tenant= query
   * param'ı eklenir ki tespit secili tenant icin yapilsin.
   */
  getLoginOptions(tenantKey?: string): Observable<{ ldapEnabled: boolean }> {
    const url = tenantKey
      ? `${environment.apiUrl}/api/identity/LoginOptions?tenant=${encodeURIComponent(tenantKey)}`
      : `${environment.apiUrl}/api/identity/LoginOptions`;
    return this.http.get<{ ldapEnabled: boolean }>(url, {
      headers: new HttpHeaders({ [this.skipErrorToastHeader]: '1' }),
    });
  }

  ssoLogin(tenant: string) {
    this.isLoadingSubject.next(true);
    if (isDevMode) {
      /* console.log('Starting SSO login for tenant:', tenant); */
    }

    // For SAML SSO, redirect directly to the SSO endpoint
    // Backend will handle the callback and redirect to Angular
    const returnUrl = localStorage.getItem('ssoReturnUrl') || '/';
    const ssoUrl = `${environment.apiUrl}/api/sso/login?tenant=${tenant}&returnUrl=${encodeURIComponent(returnUrl)}`;
    if (isDevMode) {
      /* console.log('Redirecting to SSO URL:', ssoUrl); */
    }

    // Store the return URL for after SSO authentication
    localStorage.setItem('ssoReturnUrl', window.location.origin);

    // Store SSO session info
    sessionStorage.setItem(
      'ssoSession',
      JSON.stringify({
        tenant: tenant,
        timestamp: new Date().getTime(),
      }),
    );

    // Redirect the entire window to the SSO endpoint
    window.location.href = ssoUrl;

    // Return an observable that never completes since we're redirecting
    return new Observable((observer) => {
      // This will never be called due to the redirect
      observer.next({ redirecting: true });
    });
  }

  // Refresh user data from backend
  refreshUserData(): Observable<any> {
    if (!this.userRefreshInFlight$) {
      this.isLoadingSubject.next(true);
      this.userRefreshInFlight$ = this.http
        .get<any>(`${environment.apiUrl}/api/identity/UserRefreshData`)
        .pipe(
          switchMap((userData) => {
            const currentUserString = localStorage.getItem('currentUser');
            if (!currentUserString || !userData) {
              return of(userData);
            }

            const currentUser = JSON.parse(currentUserString);
            this.revokeUserBlobAttachmentFields(currentUser);

            const updatedUser = {
              ...userData,
              token: currentUser.token,
              // UserRefreshData sadece profil/rol bilgisini tazeler — refresh-token
              // akışına ait alanları ezmemesi (ve mekanizmayı kırmaması) gerekir.
              refreshToken: currentUser.refreshToken,
              accessTokenExpiresAt: currentUser.accessTokenExpiresAt,
            };

            this.persistCurrentUserSafely(updatedUser);
            this.currentUserSubject.next(updatedUser);

            return this.enrichUserWithAttachmentBlobs(updatedUser).pipe(
              map((enriched) => {
                this.currentUserSubject.next(enriched);
                if (isDevMode) {
                  /* console.log('User data refreshed successfully'); */
                }
                return enriched;
              }),
              catchError((err) => {
                console.error('Attachment prefetch after refresh failed:', err);
                this.currentUserSubject.next(updatedUser);
                return of(updatedUser);
              }),
            );
          }),
          catchError((error) => {
            console.error('Refresh user data error:', error);
            return of(null);
          }),
          finalize(() => {
            this.isLoadingSubject.next(false);
            this.userRefreshInFlight$ = null;
          }),
          shareReplay(1),
        );
    }
    return this.userRefreshInFlight$;
  }

  /**
   * Access token'ı (JWT) refresh token ile yeniler. Backend rotating refresh token
   * kullanıyor: her başarılı çağrıda hem yeni token hem yeni refreshToken döner.
   *
   * Concurrency: aynı anda birden fazla çağrı (paralel 401'ler veya proaktif
   * zamanlayıcı) gelirse hepsi bu tek HTTP isteğinin sonucunu paylaşır.
   *
   * Refresh 401 dönerse (reuse detection ya da süresi dolmuş refresh token):
   * kullanıcı burada YENİDEN DENENMEDEN local olarak çıkış yaptırılır.
   */
  refreshToken(): Observable<UserType> {
    if (this.refreshTokenInProgress$) {
      return this.refreshTokenInProgress$;
    }

    const refreshTokenValue = this.currentUserSubject.value?.refreshToken;
    if (!refreshTokenValue) {
      this.forceLogoutInvalidSession();
      return throwError(() => new Error('No refresh token available.'));
    }

    this.refreshTokenInProgress$ = this.http
      .post<any>(this.refreshTokenEndpoint, { refreshToken: refreshTokenValue })
      .pipe(
        map((response) => {
          const updatedUser = {
            ...this.currentUserSubject.value,
            ...response,
          };
          this.persistCurrentUserSafely(updatedUser);
          this.currentUserSubject.next(updatedUser);
          this.scheduleProactiveRefresh(updatedUser);
          return updatedUser as UserType;
        }),
        catchError((error) => {
          this.forceLogoutInvalidSession();
          return throwError(() => error);
        }),
        finalize(() => {
          this.refreshTokenInProgress$ = null;
        }),
        shareReplay(1),
      );

    return this.refreshTokenInProgress$;
  }

  /**
   * RefreshToken 401 döndüğünde (çalınma şüphesi / süresi dolmuş) çağrılır.
   * Refresh token zaten geçersiz olduğu için backend'e tekrar istek atmadan,
   * sadece local oturumu temizleyip login ekranına yönlendirir.
   */
  private forceLogoutInvalidSession(): void {
    if (this.logoutInProgress) {
      return;
    }
    this.logoutInProgress = true;
    this.closeOpenOverlays();
    this.clearProactiveRefreshTimer();
    this.releaseRefreshLock();
    this.clearMfaPendingToken();
    try {
      localStorage.removeItem(this.currentUserStorageKey);
    } catch {
      /* ignore */
    }
    // Storage önce silindi; in-memory user temizliği layout henüz dururken
    // sidebar flash'ını logoutInProgress ile bastırır.
    this.currentUserSubject.next(undefined);
    this.router.navigate(['/auth/loginjtr'], {
      queryParams: { reason: 'session-expired' },
    }).then(
      () => {
        this.logoutInProgress = false;
      },
      () => {
        this.logoutInProgress = false;
      },
    );
  }

  /**
   * Access token süresi dolmadan otomatik RefreshToken çağrısı tetikler. Reaktif
   * (401) mekanizması yine de bir güvenlik ağı olarak kalır (tab arka planda
   * uyutulup zamanlayıcı kaçırılırsa diye).
   *
   * Skew (proactiveRefreshSkewMs = 2dk), token ömrünün en fazla yarısıyla
   * sınırlanır. Bu sınırlama olmadan, token ömrü skew'e yakın/eşit olduğunda
   * (örn. backend test için ömrü 2dk'ya düşürdüğünde) her yenilemeden sonra
   * hesaplanan gecikme sürekli ~0'a düşer ve minimum gecikmeye (5sn) sabitlenip
   * SONSUZA KADAR birbiri ardına refresh isteği atılmasına yol açardı.
   */
  private scheduleProactiveRefresh(user: any): void {
    this.clearProactiveRefreshTimer();

    const expiresAtRaw = user?.accessTokenExpiresAt;
    const refreshTokenValue = user?.refreshToken;
    if (!expiresAtRaw || !refreshTokenValue) {
      return;
    }

    const expiresAtMs = new Date(expiresAtRaw).getTime();
    if (Number.isNaN(expiresAtMs)) {
      return;
    }

    const totalLifetimeMs = expiresAtMs - Date.now();
    if (totalLifetimeMs <= 0) {
      // Zaten süresi dolmuş/dolmak üzere — proaktif zamanlayıcı kurmak yerine
      // reaktif (401) mekanizmasına bırak, gereksiz anlık refresh döngüsü açma.
      return;
    }

    const effectiveSkewMs = Math.min(
      this.proactiveRefreshSkewMs,
      totalLifetimeMs / 2,
    );

    const delay = Math.max(
      totalLifetimeMs - effectiveSkewMs,
      this.minProactiveRefreshDelayMs,
    );

    this.proactiveRefreshTimer = setTimeout(() => {
      this.runProactiveRefresh();
    }, delay);
  }

  private clearProactiveRefreshTimer(): void {
    if (this.proactiveRefreshTimer) {
      clearTimeout(this.proactiveRefreshTimer);
      this.proactiveRefreshTimer = null;
    }
  }

  /**
   * Zamanlayıcı tetiklendiğinde çalışır. Birden fazla tab açıksa, rotating refresh
   * token'ın aynı anda iki tab tarafından kullanılıp backend'de "reuse detection"
   * tetiklemesini önlemek için basit bir localStorage kilidi kullanılır: kilidi
   * alamayan tab kısa bir süre sonra tekrar dener (diğer tab'ın 'storage' event'i
   * ile zaten güncel token'a sahip olması beklenir).
   */
  private runProactiveRefresh(): void {
    if (!this.currentUserSubject.value?.refreshToken) {
      return;
    }

    if (!this.tryAcquireRefreshLock()) {
      this.proactiveRefreshTimer = setTimeout(
        () => this.runProactiveRefresh(),
        1500,
      );
      return;
    }

    this.refreshToken().subscribe({
      next: () => this.releaseRefreshLock(),
      error: () => this.releaseRefreshLock(),
    });
  }

  private tryAcquireRefreshLock(): boolean {
    try {
      const now = Date.now();
      const existing = Number(localStorage.getItem(this.refreshLockKey) || 0);
      if (existing > now) {
        return false;
      }
      localStorage.setItem(
        this.refreshLockKey,
        String(now + this.refreshLockTtlMs),
      );
      return true;
    } catch {
      return true;
    }
  }

  private releaseRefreshLock(): void {
    try {
      localStorage.removeItem(this.refreshLockKey);
    } catch {
      /* ignore */
    }
  }

  // Test için doğrudan backend callback'ini çağıran metod
  ssoLoginTest(tenant: string) {
    this.isLoadingSubject.next(true);
    if (isDevMode) {
      /* console.log('Starting SSO login test for tenant:', tenant); */
    }

    const returnUrl = localStorage.getItem('ssoReturnUrl') || '/';
    const callbackData = {
      tenant: tenant,
      test: true,
      returnUrl: returnUrl,
    };

    return this.processSsoCallback(callbackData);
  }

  processSsoCallback(data: any) {
    this.isLoadingSubject.next(true);
    if (isDevMode) {
      /* console.log('Processing SSO callback'); */
    }

    let request: Observable<any>;

    if (typeof data === 'string') {
      // SAML response durumu - POST request
      const callbackData = {
        SAMLResponse: data,
        RelayState: arguments[1] || '',
      };
      request = this.http.post<any>(
        `${environment.apiUrl}/api/sso/callback`,
        callbackData,
      );
    } else if (data.SAMLResponse) {
      // IDP'den gelen SAML response - POST request
      request = this.http.post<any>(
        `${environment.apiUrl}/api/sso/callback`,
        data,
      );
    } else {
      // Normal callback parametreleri - GET request
      request = this.http.get<any>(`${environment.apiUrl}/api/sso/callback`, {
        params: data,
      });
    }

    return request.pipe(
      map((response) => {
        if (isDevMode) {
          /* console.log('SSO callback response received'); */
        }

        if (response && response.success && response.token) {
          // Backend'den gelen user bilgilerini UserModel formatına dönüştür
          const userData = new UserModel();
          userData.token = response.token;
          userData.id = response.user.id;
          userData.username = response.user.email;
          userData.password = '';
          userData.fullname =
            `${response.user.firstName} ${response.user.lastName}`.trim();
          userData.email = response.user.email;
          userData.pic = './assets/media/avatars/blank.png';
          userData.roles = ['User'];
          userData.permissions = [];
          userData.occupation = '';
          userData.companyName = '';
          userData.phone = '';
          userData.firstname = response.user.firstName;
          userData.lastname = response.user.lastName;
          userData.website = '';
          userData.consignmentType = '';
          userData.language = 'en';
          userData.timeZone = '';
          userData.communication = {
            email: true,
            sms: false,
            phone: false,
          };
          userData.name =
            `${response.user.firstName} ${response.user.lastName}`.trim();
          userData.photo = './assets/media/avatars/blank.png';
          userData.branchId = response.user.companyId || '';
          userData.branchName = '';

          // Store user data
          this.persistCurrentUserSafely(userData);
          this.currentUserSubject.next(userData);

          // Display welcome toast
          setTimeout(() => {
            this.toastrService.success(
              response.message ||
                'SSO authentication successful! Welcome to Carbon Gate.',
              '👋 Welcome!',
              { closeButton: true },
            );
          }, 1000);
        }

        return response;
      }),
      catchError((err) => {
        console.error('SSO callback error:', err);
        this.isLoadingSubject.next(false);
        throw err;
      }),
      finalize(() => this.isLoadingSubject.next(false)),
    );
  }

  checkAndRegisterSsoUser(userInfo: SsoUserInfo) {
    this.isLoadingSubject.next(true);
    if (isDevMode) {
      /* console.log('Checking and registering SSO user'); */
    }

    return this.http
      .post<any>(`${environment.apiUrl}/api/sso/check-and-register`, userInfo)
      .pipe(
        map((response) => {
          if (isDevMode) {
            /* console.log('User check/registration completed'); */
          }

          if (response && response.token) {
            // Store user data
            this.persistCurrentUserSafely(response);
            this.currentUserSubject.next(response);

            // Display welcome toast
            setTimeout(() => {
              this.toastrService.success(
                'SSO authentication successful! Welcome to Carbon Gate.',
                '👋 Welcome!',
                { closeButton: true },
              );
            }, 1000);
          }

          return response;
        }),
        catchError((err) => {
          console.error('User check/registration error:', err);
          this.isLoadingSubject.next(false);
          throw err;
        }),
        finalize(() => this.isLoadingSubject.next(false)),
      );
  }

  loginConfirmEmail(
    email: string,
    password: string,
    userId: string,
    code: string,
  ) {
    let loginData = new AuthenticationConfirmEmailModel(
      email,
      password,
      userId,
      code,
    );
    this.isLoadingSubject.next(true);
    return this.http
      .post<any>(
        `${environment.apiUrl}/api/MyJtr/MyJtrLoginConfirmEmail`,
        loginData,
      )
      .pipe(
        map((response) => {
          localStorage.setItem('newEmail', response.email);
          return true;
        }),
      );

    finalize(() => this.isLoadingSubject.next(false));
  }

  /**
   * logout()'un yerel (backend'e haber verme + storage temizligi) kismi —
   * navigasyon icermez. logout() ve endStaleSessionBeforeLogin() bunu paylasir.
   */
  private clearLocalSession(): void {
    const currentUser = this.currentUserSubject.value;
    if (currentUser?.token) {
      // Fire-and-forget: backend bu kullanıcının TÜM refresh token'larını iptal
      // eder. Yanıtı beklemeden local çıkışı gerçekleştiriyoruz ki UX'te gecikme
      // olmasın; başarısız olsa da local temizlik zaten oturumu sonlandırır.
      this.http
        .post(this.logoutEndpoint, {})
        .pipe(catchError(() => of(null)))
        .subscribe();
    }

    try {
      const raw = localStorage.getItem('currentUser');
      if (raw) {
        this.revokeUserBlobAttachmentFields(JSON.parse(raw));
      }
    } catch {
      /* ignore */
    }

    this.clearProactiveRefreshTimer();
    this.releaseRefreshLock();
    this.refreshTokenInProgress$ = null;

    // Güvenlik: kalıcı token/oturum verisi hemen silinir (başka tab / F5).
    localStorage.clear();
    sessionStorage.clear();

    try {
      this.injector.get(ActiveProjectWorkflowService).clearAll();
    } catch {
      ActiveProjectWorkflowService.clearPersistedKeys();
    }

    this.userRefreshInFlight$ = null;
    this.isLoadingSubject.next(false);
    this.currentUserSubject.next(undefined);
  }

  logout() {
    if (this.logoutInProgress) {
      return;
    }
    this.logoutInProgress = true;
    this.closeOpenOverlays();

    // In-memory user + grants temizliği layout hâlâ görünürken menü flash'ına
    // yol açmasın diye logoutInProgress açık; navigasyon bitince kapanır.
    this.clearLocalSession();

    this.router.navigate(['/auth/login'], {
      queryParams: {},
    }).then(
      () => {
        this.logoutInProgress = false;
      },
      () => {
        this.logoutInProgress = false;
      },
    );
  }

  /**
   * confirm-email → set-password / forgot-password → reset-password gibi
   * akislar BASKA bir kimlik icin islem yapar. Bu akislar sonunda /auth/login'e
   * giderken, tarayicida hala BASKA bir hesabin oturumu aciksa once onu (backend'e
   * de haber vererek) kapatir — boylece login ekranindaki "zaten girisliyim"
   * otomatik yonlendirmesi (bkz. LoginjtrComponent constructor) o eski hesaba
   * geri donmez, kullanici yeni/dogrulanan hesabiyla giris yapabilir.
   *
   * logout()'tan farki: navigasyon YAPMAZ — cagiran kendi queryParams'iyla
   * (ör. reason=password-created-success) navigate eder; buyuk fire-and-forget
   * logout akisiyla yarismasin diye ayri tutuldu.
   */
  endStaleSessionBeforeLogin(): void {
    if (!this.currentUserSubject.value?.token) {
      return;
    }
    this.closeOpenOverlays();
    this.clearLocalSession();
  }

  getUserByToken(): Observable<UserType> {
    const auth = this.getAuthFromLocalStorage();

    if (!auth || !auth.authToken) {
      return of(undefined);
    }

    this.isLoadingSubject.next(true);
    return this.authHttpService.getUserByToken(auth.authToken).pipe(
      map((user: UserType) => {
        if (user) {
          this.currentUserSubject.next(user);
          this.scheduleProactiveRefresh(user);
        } else {
          this.logout();
        }
        return user;
      }),
      finalize(() => this.isLoadingSubject.next(false)),
    );
  }

  // need create new user then login
  registration(user: UserModel): Observable<any> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.createUser(user).pipe(
      map(() => {
        this.isLoadingSubject.next(false);
      }),
      switchMap(() => this.login(user.email, user.password)),
      catchError((err) => {
        console.error('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false)),
    );
  }

  forgotPassword(email: string): Observable<boolean> {
    let forgotData = new AuthenticationModel(email, '');
    this.isLoadingSubject.next(true);

    return this.http
      .post<any>(`${environment.apiUrl}/api/identity/ForgotPassword`, forgotData, {
        headers: new HttpHeaders({ [this.skipErrorToastHeader]: '1' }),
      })
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  setPassword(userId: string, password: string, confirmPassword: string) {
    let resetData = new SetPasswordModel(userId, password, confirmPassword);

    return this.http.post<any>(
      `${environment.apiUrl}/api/identity/SetPassword`,
      resetData,
    );
  }

  resetPassword(
    email: string,
    password: string,
    confirmPassword: string,
    code: string,
  ) {
    let resetData = new ResetPasswordModel(
      email,
      password,
      confirmPassword,
      code,
    );

    return this.http.post<any>(
      `${environment.apiUrl}/api/identity/ResetPassword`,
      resetData,
    );
  }

  /**
   * After UserRefreshData: one DownloadFileAttachment per distinct id, then
   * expose blob: URLs on the same fields consumers already use (photo / companyLogo).
   */
  private enrichUserWithAttachmentBlobs(user: any): Observable<any> {
    const photoId = this.attachmentGuidOrNull(user?.photoId);
    const logoId = this.attachmentGuidOrNull(user?.companyLogoId);

    if (!photoId && !logoId) {
      return of(user);
    }

    const photo$ = photoId
      ? this.fileAttachmentService.getFileAttachmentDataUrl(photoId)
      : of(null);
    const logo$ = logoId
      ? this.fileAttachmentService.getFileAttachmentDataUrl(logoId)
      : of(null);

    return forkJoin({ photoDataUrl: photo$, logoDataUrl: logo$ }).pipe(
      map(({ photoDataUrl, logoDataUrl }) => {
        const next = { ...user };
        if (photoDataUrl) {
          next.photo = photoDataUrl;
          next.photoUrl = photoDataUrl;
        }
        if (logoDataUrl) {
          next.companyLogo = logoDataUrl;
          next.companyLogoUrl = logoDataUrl;
        }
        return next;
      }),
    );
  }

  private revokeUserBlobAttachmentFields(user: any): void {
    if (!user || typeof user !== 'object') {
      return;
    }
    for (const key of ['photo', 'photoUrl', 'companyLogo', 'companyLogoUrl']) {
      const v = user[key];
      if (typeof v === 'string' && v.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(v);
        } catch {
          /* ignore */
        }
      }
    }
  }

  private attachmentGuidOrNull(value: unknown): string | null {
    if (!this.isGuid(value)) {
      return null;
    }
    const trimmed = String(value).trim();
    if (trimmed.toLowerCase() === this.emptyAttachmentGuid) {
      return null;
    }
    return trimmed;
  }

  private persistCurrentUserSafely(user: any): void {
    try {
      localStorage.setItem('currentUser', JSON.stringify(user));
      return;
    } catch {
      // Quota doluysa sadece buyuk data URL alanlarini kaldirip tekrar dener.
    }

    try {
      const fallback = { ...user };
      for (const key of [
        'photo',
        'photoUrl',
        'companyLogo',
        'companyLogoUrl',
      ]) {
        const value = fallback?.[key];
        if (typeof value === 'string' && value.startsWith('data:')) {
          fallback[key] = '';
        }
      }
      localStorage.setItem('currentUser', JSON.stringify(fallback));
    } catch {
      // no-op
    }
  }

  private isGuid(value: unknown): value is string {
    if (typeof value !== 'string') {
      return false;
    }
    const guidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return guidRegex.test(value.trim());
  }

  // private methods
  private setAuthFromLocalStorage(auth: AuthModel): boolean {
    // store auth authToken/refreshToken/epiresIn in local storage to keep user logged in between page refreshes
    if (auth && auth.authToken) {
      localStorage.setItem(this.authLocalStorageToken, JSON.stringify(auth));
      return true;
    }
    return false;
  }

  private getAuthFromLocalStorage(): AuthModel | undefined {
    try {
      const lsValue = localStorage.getItem(this.authLocalStorageToken);
      if (!lsValue) {
        return undefined;
      }

      const authData = JSON.parse(lsValue);
      return authData;
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }

  ngOnDestroy() {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
    this.clearProactiveRefreshTimer();
    if (this.companyContextCheckTimer) {
      clearTimeout(this.companyContextCheckTimer);
      this.companyContextCheckTimer = null;
    }
    if (typeof document !== 'undefined') {
      document.removeEventListener(
        'visibilitychange',
        this.onDocumentVisibilityForCompanyContext,
      );
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('focus', this.onWindowFocusForCompanyContext);
    }
  }

  confirmEmail(userid: string, code: string) {
    let confirmData = new ConfirmUserModel(userid, code);

    return this.http.post<any>(
      `${environment.apiUrl}/api/identity/ConfirmEmail`,
      confirmData,
    );
  }
  getRevealNr(revealSerialNumber: string) {
    return this.http.post<any>(`${environment.apiUrl}/api/MyJtr/GetRevealNr`, {
      revealSerialNumber,
    });
  }
  registerRevealSave(
    revealSerialNumber: string,
    company: string,
    companyOfficial: string,
    country: string,
    communicationLanguage: string,
    profession: string,
    telephone: string,
    email: string,
    purchaseDate: any,
    invoiceNr: string,
  ) {
    const URL = `${environment.apiUrl}/api/MyJtr/RegisterReveal`;
    let param = new RegisterRevealModel(
      revealSerialNumber,
      company,
      companyOfficial,
      country,
      communicationLanguage,
      profession,
      telephone,
      email,
      purchaseDate,
      invoiceNr,
    );
    return this.http.post<RegisterRevealModel[]>(URL, param);
  }

  getJtrServices() {
    const URL = `${environment.apiUrl}/api/MyJtr/GetJtrServices`;
    return this.http.get<JtrServicesModel[]>(URL);
  }

  userRegister(
    firstName: string,
    lastName: string,
    phone: string,
    email: string,
    selectedServiceType: [],
    isHaveJtrAccount: string,
    customerType: string,
    company: string,
    taxNumber: string,
    taxAdministration: string,
    idNumber: string,
    companyType: string,
    companyTitle: string,
    brand: string,
    address: string,
    city: string,
    region: string,
    postalCode: string,
    country: string,
    companyRepFirstName: string,
    companyRepLastName: string,
    companyRepPhone: string,
    companyRepEmail: string,
  ) {
    const URL = `${environment.apiUrl}/api/MyJtr/UserRegisterRequestCommand`;
    let param = {
      firstName: firstName,
      lastName: lastName,
      phone: phone,
      email: email,
      selectedServiceType: selectedServiceType,
      isHaveJtrAccount: isHaveJtrAccount,
      customerType: customerType,
      company: company,
      taxNumber: taxNumber,
      taxAdministration: taxAdministration,
      idNumber: idNumber,
      companyType: companyType,
      companyTitle: companyTitle,
      brand: brand,
      address: address,
      city: city,
      region: region,
      postalCode: postalCode,
      country: country,
      companyRepFirstName: companyRepFirstName,
      companyRepLastName: companyRepLastName,
      companyRepPhone: companyRepPhone,
      companyRepEmail: companyRepEmail,
    };
    return this.http.post<RegisterRevealModel[]>(URL, param);
  }

  registerRevealFormSave(
    companyName: string,
    companyManagement: string,
    distributor: string,
    telephoneNr: string,
    email: string,
    country: string,
    CommunicationLanguage: string,
    profession: string,
    revealSerialNr: string,
    purchaseDate: any,
    invoiceNr: string,
  ) {
    const URL = `${environment.apiUrl}/api/MyJtr/RegisterRevealForm`;
    let param = new RegisterRevealFormSaveModel(
      companyName,
      companyManagement,
      distributor,
      telephoneNr,
      email,
      country,
      CommunicationLanguage,
      profession,
      revealSerialNr,
      purchaseDate,
      invoiceNr,
    );

    return this.http.post<RegisterRevealModel[]>(URL, param);
  }
}
